import React, { createContext, useContext, useEffect } from "react";
import { useToastContext } from "./ToastContext";
import { useAuth } from "./AuthContext";

const BrandContext = createContext();

export const useBrand = () => {
  const context = useContext(BrandContext);
  if (!context) {
    throw new Error("useBrand must be used within a BrandProvider");
  }
  return context;
};

export const BrandProvider = ({
  children,
  useBusinesses,
  useBusinessStore,
}) => {
  const toast = useToastContext();
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  // Use provided hooks or create defaults
  const defaultBusinessStore = () => ({
    selectedBusiness: null,
    businessList: [],
    setSelectedBusiness: () => {},
    setBusinessList: () => {},
    hasBusinesses: () => false,
  });
  const useStoreHook = useBusinessStore || defaultBusinessStore;
  const businessStore = useStoreHook();

  const {
    selectedBusiness,
    businessList,
    setSelectedBusiness,
    setBusinessList,
    hasBusinesses,
  } = businessStore;

  const defaultBusinesses = () => ({
    data: null,
    isLoading: false,
    isError: false,
    error: null,
    refetch: () => {},
  });
  const useBusinessesHook = useBusinesses || defaultBusinesses;
  const businessesQuery = useBusinessesHook(
    {},
    { enabled: isAuthenticated && !authLoading },
  );

  const {
    data: businessesData,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = businessesQuery;

  // Update business list when API data changes
  useEffect(() => {
    if (businessesData?.result && Array.isArray(businessesData.result)) {
      if (businessesData.result.length > 0) {
        setBusinessList(businessesData.result);
      } else {
        // No businesses returned - clear storage
        setBusinessList([]);
        // Clear selected business if no businesses available
        if (selectedBusiness) {
          setSelectedBusiness(null);
        }
      }
    } else if (
      businessesData?.result &&
      !Array.isArray(businessesData.result)
    ) {
      // Handle case where result is not an array
      setBusinessList([]);
      if (selectedBusiness) {
        setSelectedBusiness(null);
      }
    }
  }, [businessesData, setBusinessList, setSelectedBusiness, selectedBusiness]);

  // Handle API errors
  useEffect(() => {
    if (isError && error) {
      console.error("Error fetching businesses:", error);
      toast.error("Failed to load businesses. Please try again.");
    }
  }, [isError, error, toast]);

  // Clear business data when user logs out
  useEffect(() => {
    if (!isAuthenticated && !authLoading) {
      setBusinessList([]);
      setSelectedBusiness(null);
    }
  }, [isAuthenticated, authLoading, setBusinessList, setSelectedBusiness]);

  const handleBrandChange = (brand) => {
    setSelectedBusiness(brand);
  };

  const handleRetry = () => {
    refetch();
  };

  // Determine the current state
  const getState = () => {
    // If not authenticated or still loading auth, don't show business states
    if (!isAuthenticated || authLoading) {
      return "loading";
    }
    if (isLoading) {
      return "loading";
    }
    if (isError) {
      return "error";
    }
    if (!hasBusinesses()) {
      // Show loading during refetch (e.g. after business creation)
      if (isFetching) return "loading";
      return "empty";
    }
    return "success";
  };

  const state = getState();

  // Provide fallback data for backward compatibility
  const brandData = businessList || [];
  const selectedBrand = selectedBusiness || null;

  // Business guard logic (consolidated here)
  const canShowBusinessContent = () => {
    // Don't show content while loading or refetching with no businesses
    if (isLoading || (!hasBusinesses() && isFetching)) return false;

    // Don't show content if there's an error
    if (isError) return false;

    // Don't show content if no businesses available
    if (!hasBusinesses()) return false;

    // Don't show content if no business is selected
    if (!selectedBrand) return false;

    return true;
  };

  const getBusinessState = () => {
    if (isLoading) return "loading";
    if (isError) return "error";
    if (!hasBusinesses()) {
      // Show loading during refetch (e.g. after business creation)
      if (isFetching) return "loading";
      return "empty";
    }
    if (!selectedBrand) return "no-selection";
    return "ready";
  };

  return (
    <BrandContext.Provider
      value={{
        selectedBrand,
        brandData,
        handleBrandChange,
        state,
        isLoading,
        isError,
        error,
        handleRetry,
        hasBusinesses: hasBusinesses(),
        canShowBusinessContent: canShowBusinessContent(),
        getBusinessState: getBusinessState(),
      }}
    >
      {children}
    </BrandContext.Provider>
  );
};
